import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useQuestions from '../../hooks/useQuestionnaireQuestions';
import Timer from '../../components/Timer/Timer';
import Question from '../../components/Question/Question';
import { authApiClient } from '../../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const QuestionnaireStart = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { questions, responseTimeLimit, loading, error } = useQuestions(id || '');

  const storageKey = `questionnaire_${id}`;
  const [answers, setAnswers] = useState(() => {
    const savedAnswers = localStorage.getItem(`${storageKey}_answers`);
    return savedAnswers ? JSON.parse(savedAnswers) : [];
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const savedIndex = localStorage.getItem(`${storageKey}_index`);
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    localStorage.setItem(`${storageKey}_answers`, JSON.stringify(answers));
    localStorage.setItem(`${storageKey}_index`, currentQuestionIndex);
  }, [answers, currentQuestionIndex, storageKey]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (answers.length > 0 && !isSubmitted) {
        event.preventDefault();
        event.returnValue = 'آیا مطمئن هستید که می‌خواهید صفحه را ترک کنید؟ پاسخ‌های شما ممکن است از دست بروند.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [answers, isSubmitted]);

  useEffect(() => {
    const preventContextMenu = (e) => e.preventDefault();
    const preventCopyPaste = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      toast.info('کپی و پیست غیرفعال است.', { position: 'top-right', autoClose: 2000 });
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
    };
  }, []);

  const clearStoredData = () => {
    localStorage.removeItem(`${storageKey}_answers`);
    localStorage.removeItem(`${storageKey}_index`);
    localStorage.removeItem(`${storageKey}_time`);
  };

  useEffect(() => {
    if (questions.length === 0) {
      setCurrentQuestionIndex(0);
    }
  }, [questions]);

  const updateAnswer = (questionId, choiceId) => {
    setAnswers((prevAnswers) => {
      const existingAnswer = prevAnswers.find((ans) => ans.question === questionId);
      if (existingAnswer) {
        const isSelected = existingAnswer.choice_answers.includes(choiceId);
        return prevAnswers.map((ans) =>
          ans.question === questionId
            ? {
                ...ans,
                choice_answers: isSelected
                  ? ans.choice_answers.filter((id) => id !== choiceId)
                  : [...ans.choice_answers, choiceId],
              }
            : ans
        );
      } else {
        return [...prevAnswers, { question: questionId, choice_answers: [choiceId] }];
      }
    });
  };

  const handleSubmit = async (isAutoSubmit = false) => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('لطفاً ابتدا وارد شوید.', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/login');
      setIsSubmitted(false);
      return;
    }

    const payload = {
      questionnaire: parseInt(id || '0', 10),
      answers,
    };

    try {
      console.log('Submitting with payload:', payload);
      const { data } = await authApiClient.post('submit/', payload);
      console.log('Submit response:', data);
      toast.success(isAutoSubmit ? 'زمان به پایان رسید، پاسخ‌ها ارسال شدند!' : 'ارسال با موفقیت انجام شد!', {
        position: 'top-right',
        autoClose: 3000,
      });
      clearStoredData();
      navigate(`/survey/questionnaires/submission/${data.submission_id}/result/`);
    } catch (err) {
      console.error('Submit error:', err);
      setIsSubmitted(false);
      const msg =
        err.response?.status === 401
          ? 'لطفاً ابتدا وارد شوید.'
          : err.response?.status === 403
          ? 'شما اجازه ارسال این پرسشنامه را ندارید.'
          : err.response?.data?.message ||
            err.response?.data?.error ||
            'خطایی در ارسال رخ داد.';
      toast.error(`خطا در ارسال: ${msg}`, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const goToPrevious = () => {
    setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
  };

  const goToNext = () => {
    setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(var(--card-bg))] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(var(--text-primary))]"></div>
      </div>
    );
  }

  if (error || !questions || questions.length === 0) {
    return (
      <div className="min-h-screen bg-[rgb(var(--card-bg))] flex items-center justify-center">
        <div className="bg-[rgb(var(--error-color))] text-[rgb(var(--card-bg))] p-6 rounded-lg">
          {error?.message || 'هیچ سوالی یافت نشد.'}
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-[rgb(var(--card-bg))] flex items-center justify-center">
        <div className="text-[rgb(var(--text-primary))]">سوال نامعتبر است.</div>
      </div>
    );
  }

  const selectedOptions =
    answers.find((ans) => ans.question === currentQuestion.id)?.choice_answers || [];

  return (
    <section className="fixed inset-0 -z-10 min-h-screen bg-[rgb(var(--card-bg))] overflow-y-auto" dir="ltr">
      <div className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <div className="text-sm text-[rgb(var(--text-secondary))] mb-2 text-left">
            سوال {currentQuestionIndex + 1} از {questions.length}
          </div>
          <div className="w-full bg-[rgb(var(--form-bg))] rounded-full h-2.5">
            <div
              className="bg-[rgb(var(--button-bg))] h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>
        <Timer
          initialTime={responseTimeLimit || 600}
          onTimeUp={() => handleSubmit(true)}
          isStopped={isSubmitted}
          storageKey={storageKey}
        />
        <motion.div
          key={currentQuestion.id}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <Question
            question={currentQuestion}
            image={currentQuestion.image}
            options={currentQuestion.options}
            selectedOptions={selectedOptions}
            onChange={(choiceId) => updateAnswer(currentQuestion.id, choiceId)}
          />
        </motion.div>

        <div className="flex justify-between mt-6">


          <button
            onClick={goToPrevious}
            aria-label="سوال قبلی"
            disabled={currentQuestionIndex === 0 || isSubmitted}
            className={`px-6 py-3 bg-[rgb(var(--form-bg))] text-[rgb(var(--text-primary))] font-semibold rounded-lg hover:bg-[rgb(var(--form-border))] transition-colors duration-200 ${
              currentQuestionIndex === 0 || isSubmitted ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            سوال قبلی
          </button>
          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={() => handleSubmit(false)}
              aria-label="ارسال پاسخ‌ها"
              disabled={isSubmitted}
              className="px-6 py-3 bg-[rgb(var(--button-bg))] text-[rgb(var(--card-bg))] font-semibold rounded-lg hover:bg-[rgb(var(--button-hover))] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ارسال پاسخ‌ها
            </button>
          ) : (
            <button
              onClick={goToNext}
              aria-label="سوال بعدی"
              disabled={isSubmitted}
              className="px-6 py-3 bg-[rgb(var(--form-bg))] text-[rgb(var(--text-primary))] font-semibold rounded-lg hover:bg-[rgb(var(--form-border))] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              سوال بعدی
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuestionnaireStart;